import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../libs/firebaseConfig";
import { buildDeviceContext, getAccessControlSettings } from "./accessControl";

const SLOT_LABELS = {
  desktop: "คอมพิวเตอร์",
  tablet: "แท็บเล็ต",
  mobile: "มือถือ",
};

export function getDeviceSlotLabel(slot) {
  return SLOT_LABELS[slot] || slot || "อุปกรณ์";
}

function isAdminRole(userDoc) {
  const role = userDoc?.role;
  if (Array.isArray(role)) {
    return role.map((x) => String(x).toLowerCase()).includes("admin");
  }
  return String(role || "").toLowerCase() === "admin";
}

export function getDeviceMonitorCacheKey(uid) {
  return `deviceMonitor:lastCheckAt:${uid}`;
}

export function shouldRunDeviceMonitor(uid, intervalHours = 6) {
  if (!uid || typeof window === "undefined") return false;

  const key = getDeviceMonitorCacheKey(uid);
  const last = Number(localStorage.getItem(key) || 0);
  const intervalMs = Math.max(1, Number(intervalHours || 6)) * 60 * 60 * 1000;

  return Date.now() - last > intervalMs;
}

export function markDeviceMonitorChecked(uid) {
  if (!uid || typeof window === "undefined") return;
  localStorage.setItem(getDeviceMonitorCacheKey(uid), String(Date.now()));
}

async function writeDeviceMonitorLog(uid, payload) {
  try {
    await addDoc(collection(db, "users", uid, "deviceMonitorLogs"), {
      ...payload,
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.warn("writeDeviceMonitorLog failed:", e?.message);
  }
}

export async function runDeviceAccessMonitor({
  user,
  userDoc,
  reason = "app_loaded",
}) {
  const uid = user?.uid;
  if (!uid) return { ok: true, skipped: true, reason: "no_user" };

  const settings = await getAccessControlSettings();

  if (!settings?.enableDeviceMonitoring) {
    return { ok: true, skipped: true, reason: "monitor_disabled", settings };
  }

  if (settings?.enableDeviceMonitoringAdminOnly === true && !isAdminRole(userDoc)) {
    return { ok: true, skipped: true, reason: "admin_only_mode", settings };
  }

  const device = await buildDeviceContext();
  const { slot, signature } = device;
  const slotLabel = getDeviceSlotLabel(slot);

  const historyRef = doc(db, "users", uid, "deviceHistory", signature);
  const historySnap = await getDoc(historyRef);
  const historyData = historySnap.exists() ? historySnap.data() : {};

  await setDoc(
    historyRef,
    {
      uid,
      slot,
      signature,
      label: device.label,

      ...(historyData?.firstSeenAt
      ? {}
      : { firstSeenAt: serverTimestamp() }),
      
      lastSeenAt: serverTimestamp(),
      seenCount: increment(1),

      lastReason: reason,

      userAgent: device.meta.userAgent,
      platform: device.meta.platform,
      language: device.meta.language,
      timezone: device.meta.timezone,
      screenSize: device.meta.screenSize,
      touchPoints: device.meta.touchPoints,

      reviewed: historyData?.reviewed || false,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  const slotSnap = await getDocs(
    query(
      collection(db, "users", uid, "deviceHistory"),
      where("slot", "==", slot)
    )
  );

  const deviceCount = slotSnap.size;
  const warningThreshold = Number(settings?.deviceWarningThreshold || 2);
  const riskThreshold = Number(settings?.deviceRiskThreshold || 3);

  let warningLevel = 0;
  if (deviceCount >= riskThreshold) warningLevel = 2;
  else if (deviceCount >= warningThreshold) warningLevel = 1;

  const alreadyAcknowledged = !!historyData?.warningAcknowledgedAt;

  const result = {
    ok: true,
    settings,
    device,
    slot,
    slotLabel,
    deviceCount,
    warningLevel,
    alreadyAcknowledged,
    shouldShowWarning:
      !!settings?.enableDeviceWarningModal &&
      warningLevel > 0 &&
      !alreadyAcknowledged,
  };

  await writeDeviceMonitorLog(uid, {
    action: "device_monitor_check",
    reason,
    result: warningLevel > 0 ? "warning" : "normal",
    slot,
    signature,
    deviceCount,
    warningLevel,
    alreadyAcknowledged,
    label: device.label,
  });

  return result;
}

export async function acknowledgeDeviceWarning({ user, result }) {
  const uid = user?.uid;
  const signature = result?.device?.signature;

  if (!uid || !signature) return;

  await setDoc(
    doc(db, "users", uid, "deviceHistory", signature),
    {
      warningAcknowledgedAt: serverTimestamp(),
      warningAcknowledgedLevel: result.warningLevel || 0,
      warningAcknowledgedDeviceCount: result.deviceCount || 0,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  await writeDeviceMonitorLog(uid, {
    action: "device_warning_acknowledged",
    result: "acknowledged",
    slot: result.slot,
    signature,
    deviceCount: result.deviceCount,
    warningLevel: result.warningLevel,
    label: result.device?.label,
  });
}